import { useState, useRef } from 'react';
import { X, Upload, CheckCircle } from 'lucide-react';
import { FeedbackItem } from '../../consultantTypes';

interface UploadNewVersionModalProps {
  visualId: string;
  visualTitle: string;
  feedbackItems: FeedbackItem[];
  onClose: () => void;
  onUpload: (file: File, resolvedFeedbackIds: string[]) => Promise<void>;
}

export function UploadNewVersionModal({
  visualId,
  visualTitle,
  feedbackItems,
  onClose,
  onUpload
}: UploadNewVersionModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFeedbackItems = feedbackItems.filter(f => f.status === 'open');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const toggleFeedback = (feedbackId: string) => {
    const newSelected = new Set(selectedFeedback);
    if (newSelected.has(feedbackId)) {
      newSelected.delete(feedbackId);
    } else {
      newSelected.add(feedbackId);
    }
    setSelectedFeedback(newSelected);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      await onUpload(selectedFile, Array.from(selectedFeedback));
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onClose();
    } catch (error) {
      console.error('Error uploading new version:', error);
      alert('Er is een fout opgetreden bij het uploaden. Probeer het opnieuw.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upload Nieuwe Versie</h2>
            <p className="text-sm text-gray-600 mt-1">{visualTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Upload afbeelding</h3>

            {!previewUrl ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium mb-1">Klik om een afbeelding te selecteren</p>
                <p className="text-sm text-gray-500">of sleep een bestand hierheen</p>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full rounded-xl border border-gray-200"
                />
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl);
                    }
                    setPreviewUrl(null);
                  }}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {openFeedbackItems.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Verwerkte feedback ({selectedFeedback.size}/{openFeedbackItems.length})
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Selecteer welke feedback je hebt verwerkt in deze nieuwe versie
              </p>

              <div className="space-y-3">
                {openFeedbackItems.map((feedback) => {
                  const isSelected = selectedFeedback.has(feedback.id);
                  return (
                    <div
                      key={feedback.id}
                      onClick={() => toggleFeedback(feedback.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                          isSelected
                            ? 'border-green-500 bg-green-500'
                            : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm ${isSelected ? 'text-green-900' : 'text-gray-700'}`}>
                            {feedback.feedbackText}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(feedback.createdAt).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {openFeedbackItems.length === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Er is geen openstaande feedback voor deze visual.
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isUploading}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Annuleren
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploaden...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload Nieuwe Versie
                </>
              )}
            </button>
          </div>
          {selectedFeedback.size > 0 && (
            <p className="text-sm text-gray-600 mt-3 text-center">
              De klant wordt automatisch geïnformeerd over de {selectedFeedback.size} verwerkte feedback{selectedFeedback.size !== 1 ? 'punten' : 'punt'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
